/**
 * AI domain event schemas.
 *
 * Typed payload validation for all AI-related Mycelium events
 * (Cortex context, specialist completions).
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module ai.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Shared sub-schemas ──────────────────────────────────────────────────

export const CortexContextPayloadSchema = z.object({
  userRole: z.string().optional(),
  permissions: z.array(z.string()).optional(),
  teams: z.array(z.string()).optional(),
  features: z.array(z.string()).optional(),
  enrichedAt: z.string().optional(),
  sessionId: z.string().optional(),
  messageCount: z.number().optional(),
  userId: z.string().optional(),
})

export const UserContextUpdatedPayloadSchema = z.object({
  context: z.record(z.unknown()),
  previousEvent: z.union([z.string(), z.record(z.unknown())]).optional(),
  updatedAt: z.string().optional(),
  source: z.string().optional(),
})

export const UserContextEnrichedPayloadSchema = z.object({
  cortexContext: CortexContextPayloadSchema.optional(),
  sessionId: z.string().optional(),
  source: z.string(),
  trigger: z.object({
    eventType: z.string().optional(),
    eventId: z.string().optional(),
  }).optional(),
})

// ── Event schemas ───────────────────────────────────────────────────────

export const UserContextUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('ai'),
  type: z.literal('user_context_updated'),
  payload: UserContextUpdatedPayloadSchema,
})

export const UserContextEnrichedEvent = BackboneEventSchema.extend({
  domain: z.literal('ai'),
  type: z.literal('user_context_enriched'),
  payload: UserContextEnrichedPayloadSchema,
})

export const CortexContextEnrichedEvent = BackboneEventSchema.extend({
  domain: z.literal('ai'),
  type: z.literal('cortex_context_enriched'),
  payload: z.object({
    cortexContext: CortexContextPayloadSchema.optional(),
    sessionId: z.string().optional(),
    messages: z.array(z.record(z.unknown())).optional(),
    source: z.string().optional(),
  }),
})

export const FilteredActionsAvailableEvent = BackboneEventSchema.extend({
  domain: z.literal('ai'),
  type: z.literal('filtered_actions_available'),
  payload: z.object({
    actions: z.array(z.record(z.unknown())),
    context: z.record(z.unknown()).optional(),
    source: z.string().optional(),
  }),
})

export const ContextResponseEvent = BackboneEventSchema.extend({
  domain: z.literal('ai'),
  type: z.literal('context_response'),
  payload: z.object({
    context: z.record(z.unknown()),
    requestEventId: z.string().optional(),
    respondedAt: z.string().optional(),
    source: z.string().optional(),
  }),
})

/**
 * Emitted by the brain's respond node when a function-calling tool
 * is executed. Tracks tool selection, execution duration, and success.
 * Used for observability and the self-improvement loop.
 */
export const AiToolExecutedEvent = BackboneEventSchema.extend({
  domain: z.literal('ai'),
  type: z.literal('ai.tool.executed'),
  payload: z.object({
    tool: z.string().describe('Function-calling tool name (e.g. web_search, manage_tasks)'),
    specialistType: z.string().optional().describe('Resolved specialist type dispatched to'),
    args: z.record(z.unknown()).optional().describe('Arguments passed to the tool'),
    success: z.boolean(),
    durationMs: z.number().optional(),
    errorMessage: z.string().optional(),
    iteration: z.number().optional().describe('Tool loop iteration (0-indexed)'),
    sessionId: z.string().optional(),
    skillSlug: z.string().optional().describe('Skill that triggered this tool, if any'),
    source: z.string().optional(),
  }),
})

/**
 * Emitted by the brain after a conversation turn completes, requesting
 * async memory extraction. Handled by event-handlers.ts which calls
 * extractMemories() + storeMemories().
 */
export const CortexMemoryExtractRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('ai'),
  type: z.literal('cortex.memory.extract_requested'),
  payload: z.object({
    sessionId: z.string().optional(),
    userId: z.string().optional(),
    messages: z.array(z.record(z.unknown())).optional(),
    skillSlug: z.string().optional(),
    source: z.string().optional(),
  }),
})

/**
 * Emitted when a skill execution completes. Tracks outcome for the
 * self-improvement loop (success_count / usage_count in agent_skills).
 */
export const CortexSkillExecutedEvent = BackboneEventSchema.extend({
  domain: z.literal('ai'),
  type: z.literal('cortex.skill.executed'),
  payload: z.object({
    skillId: z.string().optional(),
    skillSlug: z.string(),
    success: z.boolean(),
    sessionId: z.string().optional(),
    toolsUsed: z.array(z.string()).optional(),
    durationMs: z.number().optional(),
    source: z.string().optional(),
  }),
})

/**
 * Emitted when a background Cortex job is queued.
 */
export const CortexBackgroundJobQueuedEvent = BackboneEventSchema.extend({
  domain: z.literal('ai'),
  type: z.literal('cortex_background_job_queued'),
  payload: z.object({
    jobType: z.string(),
    sessionId: z.string().optional(),
    userId: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    source: z.string().optional(),
  }),
})

// ── All AI events ───────────────────────────────────────────────────────

export const AiDomainEvents = [
  UserContextUpdatedEvent,
  UserContextEnrichedEvent,
  CortexContextEnrichedEvent,
  FilteredActionsAvailableEvent,
  ContextResponseEvent,
  AiToolExecutedEvent,
  CortexMemoryExtractRequestedEvent,
  CortexSkillExecutedEvent,
  CortexBackgroundJobQueuedEvent,
] as const

// ── Brain edge HTTP bodies (non-SSE; discriminated by `event`) ──────────
// Chat turns use TanStack AI message shape — not validated here.

export const BrainSpecialistCompleteBodySchema = z.object({
  event: z.literal('specialist.complete'),
  sessionId: z.string(),
  runId: z.string().optional(),
  /** Present on full chat turns; omitted on pg_net synaptic wake-ups. */
  messages: z.array(z.record(z.unknown())).optional(),
})

export const BrainResearchCompletedBodySchema = z.object({
  event: z.literal('research.deep_research.completed'),
  sessionId: z.string(),
  userId: z.string(),
  taskId: z.string().optional(),
  query: z.string().optional(),
  reportSummary: z.string().optional(),
  organizationId: z.string().optional(),
  summary: z.string().optional(),
  /** cortex_runs.id when deep research was dispatched in await mode */
  runId: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
})

export const BrainCortexContextEnrichedBodySchema = z.object({
  event: z.literal('cortex_context_enriched'),
  sessionId: z.string().optional(),
  userId: z.string().optional(),
  messages: z.array(z.record(z.unknown())).optional(),
  cortexContext: CortexContextPayloadSchema.optional(),
  agentContext: z
    .object({
      organizationId: z.string().optional(),
    })
    .optional(),
  payload: z
    .object({
      messages: z.array(z.record(z.unknown())).optional(),
      cortexContext: CortexContextPayloadSchema.optional(),
      organizationId: z.string().optional(),
    })
    .optional(),
})

export type BrainSpecialistCompleteBody = z.infer<typeof BrainSpecialistCompleteBodySchema>
export type BrainResearchCompletedBody = z.infer<typeof BrainResearchCompletedBodySchema>
export type BrainCortexContextEnrichedBody = z.infer<typeof BrainCortexContextEnrichedBodySchema>
